import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { 
  CameraIcon, 
  ShareIcon, 
  ArrowUturnLeftIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { 
  getFunctions, 
  httpsCallable 
} from 'firebase/functions';
import { storage, db } from '../config/firebase';
import { 
  CreatePostcardForm, 
  PostcardTemplate,
  User
} from '../types';
import { useErrorHandler, ErrorModal } from '../utils/errorHandler';
import { retryService } from '../services/retryService';
import { StampImageEditor } from '../components/StampImageEditor';
import { SendAnimation3D } from '../components/SendAnimation3D';

// Postcard templates
const POSTCARD_TEMPLATES: PostcardTemplate[] = [
  {
    id: 'classic',
    name: 'Classic',
    previewImage: '/templates/classic-preview.jpg',
    borderStyle: 'classic',
    colorFilter: 'sepia(0.2)',
    fontFamily: 'serif',
    textColor: '#2c1810',
    backgroundColor: '#fef7e6',
    animation: 'fade'
  },
  {
    id: 'modern',
    name: 'Modern',
    previewImage: '/templates/modern-preview.jpg',
    borderStyle: 'modern',
    colorFilter: 'brightness(1.1) contrast(1.1)',
    fontFamily: 'sans-serif',
    textColor: '#1f2937',
    backgroundColor: '#ffffff',
    animation: 'slide'
  },
  {
    id: 'vintage',
    name: 'Vintage',
    previewImage: '/templates/vintage-preview.jpg',
    borderStyle: 'vintage',
    colorFilter: 'sepia(0.4) hue-rotate(15deg)',
    fontFamily: 'cursive',
    textColor: '#8b4513',
    backgroundColor: '#f5e6d3',
    animation: 'zoom'
  },
  {
    id: 'minimal',
    name: 'Minimal',
    previewImage: '/templates/minimal-preview.jpg',
    borderStyle: 'minimal',
    colorFilter: 'grayscale(0.1)',
    fontFamily: 'monospace',
    textColor: '#374151',
    backgroundColor: '#f9fafb',
    animation: 'none'
  }
];

export function CreatePostcardPage() {
  const { currentUser, userProfile } = useAuth();
  const { errorModal, showError, hideError } = useErrorHandler();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Reply flow detection
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [replyToSender, setReplyToSender] = useState<User | null>(null);
  
  // Retry mechanism state
  const [retryConfig] = useState({
    maxRetries: 5,
    retryInterval: 30000, // 30 seconds
    backoffMultiplier: 1.5
  });

  const [step, setStep] = useState<'upload' | 'customize' | 'details' | 'preview' | 'send'>('upload');
  const [formData, setFormData] = useState<CreatePostcardForm>({
    image: null,
    message: '',
    location: '',
    country: '',
    isPublic: false,
    tags: [],
    template: POSTCARD_TEMPLATES[0],
    emojiOverlays: [],
    sendType: 'random',
    isAnonymous: false,
    replyToId: undefined
  });
  const [imagePreview, setImagePreview] = useState('');
  const [editedImageUrl, setEditedImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [friends, setFriends] = useState<any[]>([]); // Changed type to any[] for now

  const [showSendAnimation, setShowSendAnimation] = useState(false);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<any[]>([]);
  const [isLocating, setIsLocating] = useState(false);
  const locationFetchTimeout = useRef<number | null>(null);

  useEffect(() => {
    if (currentUser) {
      fetchFriends();
      detectReplyMode();
    }
  }, [currentUser]);

  // Detect if this is a reply flow
  const detectReplyMode = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const replyToId = urlParams.get('replyTo');
    
    if (replyToId) {
      try {
        setIsReplyMode(true);
        
        // Fetch the original postcard
        const postcardDoc = await getDoc(doc(db, 'postcards', replyToId));
        if (postcardDoc.exists()) {
          const originalPostcard = postcardDoc.data();
          // setReplyToPostcard(originalPostcard); // This line was removed
          
          // Fetch the sender's info
          if (originalPostcard.createdBy) {
            const senderDoc = await getDoc(doc(db, 'users', originalPostcard.createdBy));
            if (senderDoc.exists()) {
              const senderData = senderDoc.data() as any; // Changed type to any
              setReplyToSender(senderData);
              
              // Pre-populate form for reply
              setFormData(prev => ({
                ...prev,
                sendType: 'reply',
                recipientId: originalPostcard.createdBy,
                replyToId: replyToId,
                message: `Replying to: ${originalPostcard.message}`,
                location: originalPostcard.location || '',
                country: originalPostcard.country || ''
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error detecting reply mode:', error);
        showError('Reply Error', 'Failed to load the postcard you\'re replying to');
      }
    }
  };

  const functions = getFunctions();
  const findRandomRecipient = httpsCallable(functions, 'findRandomRecipient');
  const translateMessage = httpsCallable(functions, 'translateMessage');

  const fetchFriends = async () => {
    if (!userProfile?.friends?.length) return;
    
    try {
      const friendsQuery = query(
        collection(db, 'users'),
        where('uid', 'in', userProfile.friends)
      );
      const snapshot = await getDocs(friendsQuery);
      const friendsData = snapshot.docs.map(doc => doc.data() as any); // Changed type to any
      setFriends(friendsData);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };



  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      try {
        localStorage.removeItem('editedPostcardImage');
        localStorage.removeItem('editedPostcardData');
      } catch {}
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });



  // Fetch location suggestions via Nominatim as user types
  useEffect(() => {
    if (locationFetchTimeout.current) {
      window.clearTimeout(locationFetchTimeout.current);
    }
    if (!locationQuery || locationQuery.length < 3) {
      setLocationSuggestions([]);
      return;
    }
    // Debounce requests
    locationFetchTimeout.current = window.setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(locationQuery)}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data = await res.json();
        setLocationSuggestions(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Location suggest error', e);
        setLocationSuggestions([]);
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationQuery]);

  const useMyLocation = async () => {
    if (!('geolocation' in navigator)) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${latitude}&lon=${longitude}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data = await res.json();
        const address = data?.address || {};
        const cityLike = address.city || address.town || address.village || address.hamlet || '';
        const stateLike = address.state || address.region || '';
        const country = address.country || '';
        const pretty = [cityLike, stateLike, country].filter(Boolean).join(', ');
        setFormData(prev => ({
          ...prev,
          location: pretty,
          country: country,
          coordinates: { lat: latitude, lng: longitude }
        }));
        setLocationQuery(pretty);
      } catch (e) {
        console.error('Reverse geocode error', e);
      } finally {
        setIsLocating(false);
      }
    }, (err) => {
      console.warn('Geolocation denied', err);
      setIsLocating(false);
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  };

  const generatePostcardPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imagePreview) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas size
      canvas.width = 600;
      canvas.height = 400;

      // Apply template styling
      ctx.filter = formData.template.colorFilter;
      
      // Draw background
      if (formData.template.backgroundColor) {
        ctx.fillStyle = formData.template.backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Draw image with border
      const borderWidth = 20;
      const imageWidth = canvas.width - (borderWidth * 2);
      const imageHeight = canvas.height - (borderWidth * 2);
      
      ctx.drawImage(img, borderWidth, borderWidth, imageWidth, imageHeight);

      // Draw border based on template
      ctx.strokeStyle = formData.template.textColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(borderWidth/2, borderWidth/2, canvas.width - borderWidth, canvas.height - borderWidth);

      // Draw emoji overlays
      formData.emojiOverlays.forEach(overlay => {
        ctx.font = `${24 * overlay.scale}px Arial`;
        ctx.save();
        ctx.translate(overlay.position.x, overlay.position.y);
        ctx.rotate(overlay.rotation * Math.PI / 180);
        ctx.fillText(overlay.emoji, 0, 0);
        ctx.restore();
      });

      // Draw message
      if (formData.message) {
        ctx.font = `20px ${formData.template.fontFamily}`;
        ctx.fillStyle = formData.template.textColor;
        ctx.textAlign = 'center';
        ctx.fillText(formData.message, canvas.width / 2, canvas.height - 30);
      }
    };
    img.src = imagePreview;
  };

  useEffect(() => {
    if (step === 'preview') {
      generatePostcardPreview();
    }
  }, [step, formData, imagePreview]);

  const handleSubmit = async () => {
    if (!currentUser || !formData.image) return;

    setLoading(true);
    try {
      // Get the edited image from localStorage or use original
      const editedImageFromStorage = localStorage.getItem('editedPostcardImage');
      
      let imageURL;
      
      if (editedImageFromStorage) {
        // Convert data URL to blob and upload
        const response = await fetch(editedImageFromStorage);
        const blob = await response.blob();
        const imageRef = ref(storage, `postcards/${currentUser.uid}/${Date.now()}_edited_postcard.png`);
        await uploadBytes(imageRef, blob);
        imageURL = await getDownloadURL(imageRef);
      } else {
        // Upload original image
        const imageRef = ref(storage, `postcards/${currentUser.uid}/${Date.now()}_${formData.image.name}`);
        await uploadBytes(imageRef, formData.image);
        imageURL = await getDownloadURL(imageRef);
      }

      // Translate message if needed
      let translatedMessage = formData.message;
      if (formData.message && formData.message.length > 0) {
        try {
          const translationResult = await translateMessage({ 
            message: formData.message, 
            targetLanguage: 'en' 
          });
          translatedMessage = (translationResult.data as any).translatedMessage || formData.message;
        } catch (error) {
          console.error('Translation failed, using original message:', error);
        }
      }

      // Handle different send types
      let finalRecipientId = null;
      let exchangeData = null;

      if (formData.sendType === 'friend' || formData.sendType === 'reply') {
        finalRecipientId = formData.recipientId;
      } else if (formData.sendType === 'random') {
        // For random sends, try to find recipient once, but don't block the user
        try {
          const recipientResult = await findRandomRecipient({ retryCount: 0, maxRetries: 1 });
          const recipientData = recipientResult.data as any; // Changed type to any
          
          if (recipientData.success && recipientData.recipientId) {
            finalRecipientId = recipientData.recipientId;
          } else {
            // If no recipient found immediately, save postcard with retry status
            finalRecipientId = null;
            exchangeData = {
              status: 'retrying',
              retryCount: 0,
              maxRetries: retryConfig.maxRetries,
              retryErrorMessage: 'No recipients available, will retry in background'
            };
          }
        } catch (error) {
          console.error('Error finding random recipient:', error);
          // Save postcard with retry status
          finalRecipientId = null;
          exchangeData = {
            status: 'retrying',
            retryCount: 0,
            maxRetries: retryConfig.maxRetries,
            retryErrorMessage: 'Error finding recipient, will retry in background'
          };
        }
      } else if (formData.sendType === 'social' || formData.sendType === 'blank') {
        finalRecipientId = null; // No recipient for social share or draft
      }

      // Create postcard document
      const postcardData = {
        imageURL,
        message: formData.message,
        translatedMessage,
        location: formData.location,
        country: formData.country,
        createdBy: currentUser.uid,
        recipientId: finalRecipientId,
        replyToId: formData.replyToId || null,
        createdAt: serverTimestamp(),
        isRead: false,
        isPublic: formData.isPublic,
        tags: formData.tags,
        likes: 0,
        template: formData.template,
        emojiOverlays: formData.emojiOverlays,
        isAnonymous: formData.isAnonymous,
        translations: {},
        retryStatus: exchangeData?.status === 'retrying' ? 'retrying' : 'success',
        retryCount: exchangeData?.retryCount || 0,
        maxRetries: retryConfig.maxRetries,
        retryErrorMessage: exchangeData?.retryErrorMessage || null,
        // Include coordinates if captured
        // @ts-ignore
        coordinates: (formData as any).coordinates || null
      };

      const postcardRef = await addDoc(collection(db, 'postcards'), postcardData);

      // If social share, generate a public postcard link
      if (formData.sendType === 'social') {
        const shareLink = `${window.location.origin}/postcard/${postcardRef.id}`;
        await updateDoc(postcardRef, { shareLink });
      }

      // Create exchange record for friend, random, and reply sends
      if (formData.sendType === 'friend' || formData.sendType === 'random' || formData.sendType === 'reply') {
        const exchangeRecord = {
          senderId: currentUser.uid,
          recipientId: finalRecipientId,
          postcardId: postcardRef.id,
          status: exchangeData?.status || 'sent',
          sentAt: serverTimestamp(),
          retryCount: exchangeData?.retryCount || 0,
          maxRetries: retryConfig.maxRetries,
          retryErrorMessage: exchangeData?.retryErrorMessage || null
        };
        const exchangeRef = await addDoc(collection(db, 'postcardExchanges'), exchangeRecord);
        
        // If postcard needs retrying, add to background retry queue
        if (exchangeData?.status === 'retrying') {
          retryService.addToRetryQueue({
            postcardId: postcardRef.id,
            exchangeId: exchangeRef.id,
            maxRetries: retryConfig.maxRetries,
            onSuccess: (result) => {
              console.log(`Background retry succeeded for postcard ${postcardRef.id}:`, result);
            },
            onError: (error) => {
              console.error(`Background retry failed for postcard ${postcardRef.id}:`, error);
            },
            onComplete: (result) => {
              console.log(`Background retry completed for postcard ${postcardRef.id}:`, result);
            }
          });
        }
      }

      // Update user stats
      if (userProfile) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          'stats.postcardsSent': userProfile.stats.postcardsSent + 1
        });
      }

      // Play send animation
      setShowSendAnimation(true);
      
    } catch (error) {
      console.error('Error sending postcard:', error);
      showError('Send Error', 'Failed to send postcard. Please try again.', {
        label: 'Go to Dashboard',
        onClick: () => {
          window.location.href = '/dashboard';
        }
      });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">Create Postcard</h1>
          <p className="text-blue-100">
            Share your travel moments with the world
          </p>
        </motion.div>
      </div>

      {/* Progress Steps */}
      <div className="card">
        <div className="flex items-center justify-between mb-6 overflow-x-auto">
          {['upload', 'customize', 'details', 'preview', 'send'].map((stepName, index) => (
            <div key={stepName} className="flex items-center flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === stepName 
                  ? 'bg-blue-600 text-white' 
                  : index < ['upload', 'customize', 'details', 'preview', 'send'].indexOf(step)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
              }`}>
                {index + 1}
              </div>
              {index < 4 && (
                <div className={`w-8 sm:w-12 md:w-16 h-1 mx-1 sm:mx-2 ${
                  index < ['upload', 'customize', 'details', 'preview', 'send'].indexOf(step)
                    ? 'bg-green-500'
                    : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Upload Image */}
      {step === 'upload' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Upload Your Photo</h2>
          
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <CameraIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            {imagePreview ? (
              <div>
                <img src={imagePreview} alt="Preview" className="mx-auto max-h-64 rounded-lg mb-4" />
                <div className="flex space-x-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setStep('customize')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Continue to Customize
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview('');
                      setFormData(prev => ({ ...prev, image: null }));
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {isDragActive ? 'Drop your image here' : 'Drag & drop your image here'}
                </p>
                <p className="text-sm text-gray-600">or click to browse</p>
                <p className="text-xs text-gray-500 mt-2">Supports JPG, PNG, GIF up to 5MB</p>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep('customize')}
              disabled={!imagePreview}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Customize
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Customize */}
      {step === 'customize' && (
        <StampImageEditor
          imageUrl={imagePreview}
          onSave={(editedImageUrl) => {
            setEditedImageUrl(editedImageUrl);
            setStep('details');
          }}
          onBack={() => setStep('upload')}
        />
      )}

      {/* Step 3: Details */}
      {step === 'details' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Add Details</h2>

          {/* Edited image preview */}
          <div className="flex justify-center mb-6">
            {editedImageUrl ? (
              <img
                src={editedImageUrl}
                alt="Edited Postcard"
                className="max-w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: '400px' }}
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={formData.message}
                maxLength={300}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full h-28 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Write a short message (max 300 chars)"
              />
              <div className="mt-1 text-xs text-gray-500 text-right">{formData.message.length}/300</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={locationQuery}
                  onChange={(e) => {
                    setLocationQuery(e.target.value);
                    setFormData(prev => ({ ...prev, location: e.target.value }));
                  }}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City, Region, Country"
                />
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={isLocating}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {isLocating ? 'Locating...' : 'Use my location'}
                </button>
              </div>
              {locationSuggestions.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto bg-white shadow">
                  {locationSuggestions.map((s) => {
                    const address = s.address || {};
                    const cityLike = address.city || address.town || address.village || address.hamlet || '';
                    const stateLike = address.state || address.region || '';
                    const country = address.country || '';
                    const label = s.display_name as string;
                    return (
                      <div
                        key={`${s.lat}-${s.lon}-${label}`}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          const pretty = [cityLike, stateLike, country].filter(Boolean).join(', ');
                            setFormData(prev => ({
                              ...prev,
                              location: pretty || label,
                              country: country || prev.country,
                              coordinates: { lat: parseFloat(s.lat), lng: parseFloat(s.lon) }
                            }));
                          setLocationQuery(pretty || label);
                          setLocationSuggestions([]);
                        }}
                      >
                        <div className="text-sm text-gray-900">{cityLike || label}</div>
                        <div className="text-xs text-gray-500">{label}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Country"
              />
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              onClick={() => setStep('customize')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => setStep('preview')}
              disabled={!editedImageUrl || !formData.location || !formData.country}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Preview
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Preview Your Postcard</h2>
          
          <div className="flex justify-center mb-6">
            {editedImageUrl ? (
              <div className="w-[320px] sm:w-[360px] md:w-[400px]" onClick={() => setPreviewFlipped(!previewFlipped)}>
                <div className="relative [perspective:1000px]">
                  <div className={`relative h-[400px] sm:h-[450px] md:h-[500px] [transform-style:preserve-3d] transition-transform duration-500 ${previewFlipped ? '[transform:rotateY(180deg)]' : ''}`}>
                    {/* Front */}
                    <div className="absolute inset-0 [backface-visibility:hidden] bg-white rounded-lg shadow-xl overflow-hidden">
                      <div className="relative w-full h-full">
                        <div className="absolute inset-0" style={{ aspectRatio: '4 / 5' }}>
                          <img src={editedImageUrl} alt="Postcard" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    </div>
                    {/* Back */}
                    <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white rounded-lg shadow-xl p-4 flex flex-col justify-between">
                      <div>
                        <div className="text-sm text-gray-500">Tap to flip</div>
                        <div className="mt-2 text-gray-800 whitespace-pre-wrap">{formData.message || 'No message'}</div>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center justify-between">
                        <span>{formData.location}{formData.country ? `, ${formData.country}` : ''}</span>
                        <span>4×5</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No edited image found. Please go back and edit your image.</p>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('details')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => setStep('send')}
              disabled={!editedImageUrl}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Send
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Send Options */}
      {step === 'send' && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {isReplyMode ? 'Send Reply' : 'Choose How to Send'}
          </h2>
          
          {/* Reply Mode Info */}
          {isReplyMode && replyToSender && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <ArrowUturnLeftIcon className="h-5 w-5 text-blue-600 mr-2" />
                <div>
                  <p className="font-medium text-blue-800">Replying to {replyToSender.displayName}</p>
                  <p className="text-sm text-blue-700">Your postcard will be sent as a reply</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Send Type Selection - Only show if not in reply mode */}
            {!isReplyMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => setFormData(prev => ({ ...prev, sendType: 'random' }))}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendType === 'random'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <GlobeAltIcon className="h-6 w-6 text-blue-600 mr-2" />
                    <h3 className="font-semibold">Send to Random User</h3>
                  </div>
                  <p className="text-sm text-gray-600">Your postcard will be sent to a random traveler worldwide</p>
                </div>

                <div
                  onClick={() => setFormData(prev => ({ ...prev, sendType: 'friend' }))}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendType === 'friend'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <UserGroupIcon className="h-6 w-6 text-green-600 mr-2" />
                    <h3 className="font-semibold">Send to Friend</h3>
                  </div>
                  <p className="text-sm text-gray-600">Send directly to someone in your friends list</p>
                </div>

                <div
                  onClick={() => setFormData(prev => ({ ...prev, sendType: 'social' }))}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendType === 'social'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <ShareIcon className="h-6 w-6 text-purple-600 mr-2" />
                    <h3 className="font-semibold">Share Link</h3>
                  </div>
                  <p className="text-sm text-gray-600">Create a shareable link for social media</p>
                </div>

                <div
                  onClick={() => setFormData(prev => ({ ...prev, sendType: 'blank' }))}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendType === 'blank'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center mb-2">
                    <SparklesIcon className="h-6 w-6 text-yellow-600 mr-2" />
                    <h3 className="font-semibold">Save as Draft</h3>
                  </div>
                  <p className="text-sm text-gray-600">Save to your collection without sending</p>
                </div>
              </div>
            )}

            {/* Friend Selection */}
            {formData.sendType === 'friend' && friends.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Select Friend</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {friends.map((friend) => (
                    <div
                      key={friend.uid}
                      onClick={() => setFormData(prev => ({ ...prev, recipientId: friend.uid }))}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.recipientId === friend.uid
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <img
                          src={friend.photoURL || '/default-avatar.png'}
                          alt={friend.displayName}
                          className="w-8 h-8 rounded-full mr-3"
                        />
                        <div>
                          <p className="font-medium">{friend.displayName}</p>
                          <p className="text-sm text-gray-600">{friend.homeCity}, {friend.homeCountry}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Anonymous Option - Only show if not in reply mode */}
            {!isReplyMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                  className="mr-3"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700">
                  Send anonymously (mystery postcard)
                </label>
              </div>
            )}

            {/* Public Option */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="public"
                checked={formData.isPublic}
                onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="mr-3"
              />
              <label htmlFor="public" className="text-sm text-gray-700">
                Make this postcard public (visible in discover feed)
              </label>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              onClick={() => setStep('preview')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || (formData.sendType === 'friend' && !formData.recipientId)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  {isReplyMode ? 'Send Reply' : 'Send Postcard'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        action={errorModal.action}
        onClose={hideError}
      />

      {/* Advanced Image Editor */}
      {/* The StampImageEditor component is now integrated into the customize step */}

             {/* 3D Send Animation */}
       <SendAnimation3D
         isVisible={showSendAnimation}
         postcardImageUrl={editedImageUrl || ''}
         onComplete={() => {
           setShowSendAnimation(false);
           // Reset form and go to success
           setFormData({
             image: null,
             message: '',
             location: '',
             country: '',
             isPublic: false,
             tags: [],
             template: POSTCARD_TEMPLATES[0],
             emojiOverlays: [],
             sendType: 'random',
             isAnonymous: false,
             replyToId: null
           });
           setImagePreview('');
           setStep('upload');
           setIsReplyMode(false);
           setReplyToSender(null);
           // Clear cached edited image
           try {
             localStorage.removeItem('editedPostcardImage');
             localStorage.removeItem('editedPostcardData');
           } catch {}
           
           // Show success message and redirect to dashboard
           const successMessage = formData.sendType === 'reply' 
             ? 'Reply sent successfully!'
             : 'Postcard sent successfully!';
           
           console.log(successMessage);
           
           // Redirect to dashboard after successful send
           window.location.href = '/dashboard';
         }}
       />
    </div>
  );
} 